/**
 * Subscribe to the shared server playback session (REST + SSE).
 */
import { useEffect } from "react";
import type { PlaybackSnapshot } from "@memes/shared";
import { playbackApi } from "../lib/api";
import { clientLog } from "../lib/clientLogger";
import {
  applyPlaybackSnapshot,
  noteServerClockSample,
  resetPlaybackSyncRevision,
  setPlaybackTracksCatalog,
} from "../lib/playbackSync";
import { useTracks } from "./useTracks";

export function usePlaybackSync(): void {
  const { data: tracks = [] } = useTracks();

  useEffect(() => {
    setPlaybackTracksCatalog(tracks);
  }, [tracks]);

  useEffect(() => {
    let closed = false;
    let es: EventSource | null = null;
    resetPlaybackSyncRevision();

    const apply = (snap: PlaybackSnapshot): void => {
      if (closed) return;
      applyPlaybackSnapshot(snap);
    };

    const fetchAndApply = async (): Promise<void> => {
      try {
        const send = Date.now();
        const snap = await playbackApi.get();
        const recv = Date.now();
        if (typeof snap.serverTimeMs === "number") {
          noteServerClockSample({
            clientSendMs: send,
            serverTimeMs: snap.serverTimeMs,
            clientRecvMs: recv,
          });
        }
        apply(snap);
      } catch (err) {
        clientLog.error("playback", "Failed to load playback snapshot", {
          err: String(err),
        });
      }
    };

    void fetchAndApply();

    es = new EventSource(playbackApi.eventsUrl(), { withCredentials: true });
    es.addEventListener("snapshot", (ev) => {
      try {
        const snap = JSON.parse((ev as MessageEvent).data) as PlaybackSnapshot;
        if (typeof snap.serverTimeMs === "number") {
          const now = Date.now();
          noteServerClockSample({
            clientSendMs: now,
            serverTimeMs: snap.serverTimeMs,
            clientRecvMs: now,
          });
        }
        apply(snap);
      } catch (err) {
        clientLog.error("playback", "Invalid playback SSE payload", { err: String(err) });
      }
    });

    const onFocus = (): void => {
      void fetchAndApply();
    };
    window.addEventListener("focus", onFocus);

    return () => {
      closed = true;
      es?.close();
      window.removeEventListener("focus", onFocus);
    };
  }, []);
}
